/* eslint-disable @typescript-eslint/no-var-requires */
import React, { useState, useEffect } from 'react';
import { isEqual } from 'lodash';

import GraphTable from '../../../components/GraphTable';
import {
  primaryFilterOptions,
  limitedFilterOptions,
  filterOptionsWithoutSubcateg
} from '../../../components/GraphTable/configs';

import { CopyData, GraphTableData, GraphTableDataObject, ModalFilter } from '../../../types';

import { formatDateFilter } from '../../../helpers/format';
import { returnParams, fetchEndpoint } from '../helpers';
import { endpoints, handleFetchEndpoint } from '../configs';

import { useSelector } from 'react-redux';
import { ReduxStates } from '../../../redux/reduxTypes';
import { demoSelectedGrid } from '../../../helpers/demoValues';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
  marketFilters: (ModalFilter | null)[];
  chosenSegmentIndex: number;
  selectedCurrency: string | null;
  dateFilter: string;
  customRangeFilter?: (number | string)[] | null;
  setStatus: (arg1: any) => void;
  copyData: CopyData;
  showWidgets?: boolean;
}

const SectionDetailGrid = ({
  marketFilters,
  chosenSegmentIndex,
  selectedCurrency,
  dateFilter,
  customRangeFilter,
  setStatus,
  copyData,
  showWidgets
}: Props): React.ReactElement => {
  const client = useSelector((state: ReduxStates) => state.client);
  const data = useSelector((state: ReduxStates) => state.data);

  const [tableData, setTableData] = useState<Array<any> | null>(null);
  const [selectedTableData, setSelectedTableData] = useState<Array<GraphTableData> | null>(null);
  const [gridDataUnavailable, setGridDataUnavailable] = useState<boolean>(false);
  const [fetchingTables, setFetchingTables] = useState<string[]>([]);
  const [groupingOption, setGroupingOption] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>(dateFilter);
  const [loading, setLoading] = useState<boolean>(false);
  const path = location.pathname.split('/')[2].replace(/-/gi, '').toString();
  const [options, setOptions] = useState<string[]>([]);
  const { oktaAuth } = useOktaAuth();

  const fetchGridData = (path: string | number, params: unknown) => {
    const target = endpoints[path].grid.filter((e) => e.index === chosenSegmentIndex);
    if (!target.length) {
      !tableData && setTableData([]);
      return;
    }

    target.forEach(({ endpoint, id, title }) => {
      // if (fetchingTables.includes(endpoint)) return;
      (async () => {
        try {
          if (!(!showWidgets && id === 'mediaSPGR')) {
            setFetchingTables((fetchingTables: string[]) => [...fetchingTables, endpoint]);
            const data = await fetchEndpoint(endpoint, params, title, oktaAuth);
            setTableData(() => [...handleFetchEndpoint[path].grid(id, data)]);
            setFetchingTables(fetchingTables.filter((e) => e !== endpoint));
          }
        } catch (error) {
          setFetchingTables(fetchingTables.filter((e) => e !== endpoint));
          if (!tableData || (tableData && tableData.length)) setTableData([]);
          console.error(error);
          setStatus({ state: 'error', message: 'Something went wrong fetching Table data. Please try again.' });
        }
      })();
    });
  };

  const handleTableUpdate = () => {
    let selectedTable;

    if (tableData) {
      const table: GraphTableDataObject[] = tableData.filter(
        (table: GraphTableDataObject) => table.index === chosenSegmentIndex
      );
      selectedTable = table;
    } else {
      selectedTable = null;
    }

    if (tableData?.length && selectedTable?.length) {
      if (selectedTable[0].data) {
        if (client === 'demo' && selectedTable[0].title === 'Spend As % of Gross <br/> Revenue') {
          switch (dateFilter) {
            case 'This Month':
              selectedTable = demoSelectedGrid[0];
              break;
            case 'Last Month':
              selectedTable = demoSelectedGrid[1];
              break;
            case 'Last 3 Months':
              selectedTable = demoSelectedGrid[2];
              break;
            case 'Last 6 Months':
              selectedTable = demoSelectedGrid[3];
              break;
            case 'Last 12 Months':
              selectedTable = demoSelectedGrid[4];
              break;
            case 'Year to Date':
              selectedTable = demoSelectedGrid[5];
              break;
            default:
              selectedTable = demoSelectedGrid[5];
          }
        }
        isEqual(selectedTable[0].data, selectedTableData)
          ? null
          : setSelectedTableData(selectedTable.flatMap((item: any) => item.data));
      } else {
        setGridDataUnavailable(true);
      }
    } else if (tableData && !tableData.length) {
      setSelectedTableData([]);
    }
  };

  const resetTableData = () => {
    setGridDataUnavailable(false);
    setTableData(null);
    setSelectedTableData(null);
  };

  // fetch data
  useEffect(() => {
    //reset grid data
    resetTableData();
    setLoading(true);

    if (groupingOption && selectedCurrency && dateFilter && data.dateFilterRangeLimit) {
      const { params, path } = returnParams(
        marketFilters,
        formatDateFilter(dateFilter, customRangeFilter || undefined, copyData, data.dateFilterRangeLimit),
        data.dateFilterRangeLimit,
        groupingOption
      );
      if (!(path === 'sales' && groupingOption === 'SubCategory' && chosenSegmentIndex === 0)) {
        fetchGridData(path, params);
      }
    }
  }, [
    selectedCurrency,
    marketFilters,
    chosenSegmentIndex,
    groupingOption,
    dateFilter,
    customRangeFilter,
    data.dateFilterRangeLimit
  ]);

  // data updated
  useEffect(() => {
    if (tableData) {
      setLoading(false);
    }
    handleTableUpdate();
  }, [tableData]);

  useEffect(() => {
    const option =
      tableData && tableData.length && tableData[0].limitedOptions
        ? limitedFilterOptions
        : path === 'media'
        ? filterOptionsWithoutSubcateg
        : path === 'digitalcommercepenetration'
        ? limitedFilterOptions
        : path === 'sales' && chosenSegmentIndex === 0
        ? filterOptionsWithoutSubcateg
        : primaryFilterOptions;
    setOptions(option);
  }, [tableData, chosenSegmentIndex]);

  useEffect(() => {
    const date =
      dateFilter.toLocaleLowerCase() !== 'custom range'
        ? dateFilter
        : formatDateFilter(dateFilter, customRangeFilter || undefined, copyData, data.dateFilterRangeLimit);
    setTimeRange(date);
  }, [dateFilter, customRangeFilter]);

  return (
    <GraphTable
      selectedCurrency={selectedCurrency || ''}
      timeRange={timeRange}
      data={selectedTableData}
      onOptionChange={setGroupingOption}
      unavailableData={gridDataUnavailable}
      loading={loading}
      options={options}
    />
  );
};

export default SectionDetailGrid;
